/**
 * Offline-first Board Post Service
 *
 * Handles caching, offline creation, and syncing of board posts.
 * Uses IndexedDB (Dexie) for local storage and syncs to the server when online.
 */

import { db, CachedBoardPost } from './db';
import { api } from '@/services/api';
import { networkService } from '@/services/network.service';
import { useTenantStore } from '@/store/tenant.store';
import { CreateBoardPostData, BoardPostWithDetails, BoardPostFilters, BoardClassification, BoardPriority } from '@/types/board';
import { clientIdFor, isDeletedReplay, originatorStamp, mustDeferSend, sendTenantOf } from './client-id';
import { resolveSyncableIncidentId } from './incident-offline.service';

/**
 * Read the currently-active tenant id (or null if none / SSR).
 */
function getActiveTenantId(): string | null {
  try {
    return useTenantStore.getState().activeTenant?.id ?? null;
  } catch {
    return null;
  }
}

export interface BoardPostSyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

class BoardOfflineService {
  /**
   * Create a new board post (works offline).
   * If online, attempts immediate sync. Otherwise, stores locally for later sync.
   */
  async create(
    tenantId: string,
    input: CreateBoardPostData
  ): Promise<CachedBoardPost> {
    // P2-05: the client id becomes the server post's id (idempotent create),
    // and the item records who created it, in which tenant and device session.
    const stamp = originatorStamp(tenantId);
    const offlineId = `offline_${stamp.client_id}`;
    const now = stamp.occurred_at;

    const post: CachedBoardPost = {
      ...stamp,
      id: offlineId,
      offline_id: offlineId,
      tenant_id: tenantId,
      incident_id: input.incident_id || null,
      title: input.title,
      content: input.content,
      summary: input.summary,
      classification: input.classification || 'internal',
      priority: input.priority || 'normal',
      target_audience: input.target_audience || ['all'],
      target_units: input.target_units || [],
      effective_at: input.effective_at,
      expires_at: input.expires_at,
      is_pinned: input.is_pinned || false,
      pinned_until: input.pinned_until,
      author_unit: input.author_unit,
      is_published: input.is_published ?? true,
      tags: input.tags || [],
      acknowledgement_count: 0,
      comment_count: 0,
      created_at: now,
      updated_at: now,
      is_synced: false,
      pending_changes: true,
    };

    await db.cachedBoardPosts.add(post);

    // Try to sync immediately if online
    if (networkService.isOnline()) {
      await this.syncPost(offlineId);
    }

    return post;
  }

  /**
   * Update an existing board post (works offline).
   */
  async update(
    postId: string,
    updates: Partial<CreateBoardPostData>
  ): Promise<CachedBoardPost | null> {
    const existing = await db.cachedBoardPosts.get(postId);
    if (!existing) {
      // Try finding by offline_id
      const byOfflineId = await db.cachedBoardPosts
        .where('offline_id')
        .equals(postId)
        .first();
      if (!byOfflineId) return null;
      return this.update(byOfflineId.id, updates);
    }

    const updated: CachedBoardPost = {
      ...existing,
      ...updates,
      incident_id: updates.incident_id !== undefined ? (updates.incident_id || null) : existing.incident_id,
      tags: updates.tags || existing.tags,
      updated_at: new Date().toISOString(),
      is_synced: false,
      pending_changes: true,
    };

    await db.cachedBoardPosts.put(updated);

    // Try to sync if online
    if (networkService.isOnline()) {
      await this.syncPost(updated.id);
    }

    return updated;
  }

  /**
   * Get all cached board posts for a tenant, with optional filtering.
   */
  async getCachedPosts(
    tenantId: string,
    filters?: BoardPostFilters
  ): Promise<CachedBoardPost[]> {
    let results = await db.cachedBoardPosts
      .where('tenant_id')
      .equals(tenantId)
      .toArray();

    // Apply optional filters in memory
    if (filters) {
      if (filters.incident_id !== undefined) {
        results = results.filter(p => p.incident_id === filters.incident_id);
      }

      if (filters.classification?.length) {
        results = results.filter(p => filters.classification!.includes(p.classification as BoardClassification));
      }

      if (filters.priority?.length) {
        results = results.filter(p => filters.priority!.includes(p.priority as BoardPriority));
      }

      if (filters.is_pinned) {
        results = results.filter(p => p.is_pinned);
      }

      if (filters.tags?.length) {
        results = results.filter(p =>
          p.tags.some(t => filters.tags!.includes(t))
        );
      }

      if (filters.search) {
        const search = filters.search.toLowerCase();
        results = results.filter(
          p =>
            p.title.toLowerCase().includes(search) ||
            p.content.toLowerCase().includes(search) ||
            (p.summary && p.summary.toLowerCase().includes(search))
        );
      }

      if (!filters.show_expired) {
        const now = new Date();
        results = results.filter(
          p => !p.expires_at || new Date(p.expires_at) > now
        );
      }
    }

    // Sort: pinned first, then by created_at descending
    results.sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    return results;
  }

  /**
   * Get a single cached post by ID.
   */
  async getById(postId: string): Promise<CachedBoardPost | undefined> {
    let post = await db.cachedBoardPosts.get(postId);
    if (!post) {
      // Try finding by offline_id
      post = await db.cachedBoardPosts
        .where('offline_id')
        .equals(postId)
        .first();
    }
    return post;
  }

  /**
   * Get pending (unsynced) posts.
   */
  async getPending(): Promise<CachedBoardPost[]> {
    return db.cachedBoardPosts
      // Loose check — offline posts are written is_synced:false (boolean) and
      // IndexedDB can't index booleans, so an index .equals(0) misses them
      // (they showed as pending via the loose count but never synced).
      .filter(p => !p.is_synced)
      .toArray();
  }

  /**
   * Get count of pending items.
   */
  async getPendingCount(): Promise<number> {
    return db.cachedBoardPosts.filter(p => !p.is_synced).count();
  }

  /**
   * Cache board posts from the server for offline access.
   *
   * Conflict resolution strategy:
   * - If local has pending changes, preserve local version (user's work takes priority)
   * - If local is synced and server is newer, update from server
   * - If local is synced and server is same/older, keep local (already up to date)
   */
  async cacheFromServer(
    tenantId: string,
    incidentId?: string
  ): Promise<{ cached: number; conflicts: number }> {
    const result = { cached: 0, conflicts: 0 };

    try {
      const params: Record<string, string> = { limit: '100' };
      if (incidentId) params.incident_id = incidentId;

      const response = await api.get<{
        data?: BoardPostWithDetails[];
        posts?: BoardPostWithDetails[];
      }>('/board', params);

      const serverPosts = response.data || response.posts || [];

      for (const serverPost of serverPosts) {
        const existing = await db.cachedBoardPosts.get(serverPost.id);

        if (!existing) {
          // New post from server -- cache it
          await db.cachedBoardPosts.put(
            this.mapServerPostToCache(serverPost, tenantId)
          );
          result.cached++;
        } else if (existing.pending_changes) {
          // Local has unsaved changes -- preserve local version
          result.conflicts++;
          console.info(
            `[BoardOffline] Conflict detected for ${serverPost.id} - preserving local changes`
          );
        } else if (
          new Date(serverPost.updated_at) > new Date(existing.updated_at)
        ) {
          // Server version is newer and local has no pending changes -- update
          await db.cachedBoardPosts.put(
            this.mapServerPostToCache(serverPost, tenantId)
          );
          result.cached++;
        }
        // else: Local is synced and up-to-date, no action needed
      }
    } catch (error) {
      console.warn('[BoardOffline] Failed to cache from server:', error);
      // Don't throw -- offline mode should continue to work
    }

    return result;
  }

  /**
   * Sync a single post to the server.
   */
  /**
   * P2-05: a pending post that must wait: created by another user on this
   * device, or new under an offline-created incident that has not synced.
   */
  async isDeferred(post: CachedBoardPost): Promise<boolean> {
    if (mustDeferSend(post)) return true;
    const isNew = !!post.offline_id && post.id === post.offline_id;
    return isNew && (await resolveSyncableIncidentId(post.incident_id)).defer;
  }

  async syncPost(postId: string): Promise<boolean> {
    const post = await this.getById(postId);
    if (!post || post.is_synced) return true;
    if (await this.isDeferred(post)) return false;

    try {
      let serverId = post.id;

      // Check if this is a new offline post
      if (post.offline_id && post.id === post.offline_id) {
        const resolved = await resolveSyncableIncidentId(post.incident_id);
        if (resolved.defer) return false;
        // Create on server. P2-05: `id` is the client id, so a replay returns
        // the existing post (200) instead of posting it twice. incident_id is
        // omitted, never null, when the post has none (the schema rejects null).
        let response: { id?: string; data?: { id: string } };
        try {
          response = await api.post<{ id?: string; data?: { id: string } }>('/board', {
          id: clientIdFor(post),
          title: post.title,
          content: post.content,
          summary: post.summary,
          classification: post.classification,
          priority: post.priority,
          target_audience: post.target_audience,
          target_units: post.target_units,
          effective_at: post.effective_at,
          expires_at: post.expires_at,
          is_pinned: post.is_pinned,
          pinned_until: post.pinned_until,
          author_unit: post.author_unit,
          is_published: post.is_published,
          tags: post.tags,
          incident_id: resolved.incidentId ?? undefined,
          }, sendTenantOf(post));
        } catch (createError) {
          if (isDeletedReplay(createError)) {
            // The create landed earlier and the post was deleted since: done.
            await db.cachedBoardPosts.delete(post.id);
            return true;
          }
          throw createError;
        }

        serverId = response.data?.id || response.id || post.id;

        // Atomic delete-then-put to prevent data loss if put fails between the
        // two ops (offline id removed but server-id record never written).
        await db.transaction('rw', db.cachedBoardPosts, async () => {
          await db.cachedBoardPosts.delete(post.id);
          await db.cachedBoardPosts.put({
            ...post,
            id: serverId,
            incident_id: resolved.incidentId,
            is_synced: true,
            synced_at: new Date().toISOString(),
            pending_changes: false,
            last_error: undefined,
          });
        });
      } else {
        // Update existing on server
        await api.put(`/board/${post.id}`, {
          title: post.title,
          content: post.content,
          summary: post.summary,
          classification: post.classification,
          priority: post.priority,
          target_audience: post.target_audience,
          target_units: post.target_units,
          effective_at: post.effective_at,
          expires_at: post.expires_at,
          is_pinned: post.is_pinned,
          pinned_until: post.pinned_until,
          author_unit: post.author_unit,
          is_published: post.is_published,
          tags: post.tags,
        }, sendTenantOf(post));

        await db.cachedBoardPosts.update(post.id, {
          is_synced: true,
          synced_at: new Date().toISOString(),
          pending_changes: false,
          last_error: undefined,
        });
      }

      return true;
    } catch (error) {
      console.error('[BoardOffline] Sync failed:', error);
      await db.cachedBoardPosts.update(post.id, {
        last_error: (error as Error).message,
      });
      return false;
    }
  }

  /**
   * Sync all pending posts to the server, then refresh cache from server.
   */
  async syncAll(tenantId: string): Promise<BoardPostSyncResult> {
    const result: BoardPostSyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    // First, push any pending local posts to the server
    const pending = await this.getPending();
    const activeTenantId = getActiveTenantId();

    for (const post of pending) {
      // Tenant guard: getPending() ignores the tenantId param, so a post from a
      // different tenant could be replayed against the ambient X-Tenant-ID after
      // a switch. Defer (leave pending) any record whose tenant isn't active.
      if (post.tenant_id != null && activeTenantId != null && post.tenant_id !== activeTenantId) {
        continue;
      }
      // Another user's post, or waiting for its offline incident: leave pending.
      if (await this.isDeferred(post)) continue;
      const success = await this.syncPost(post.id);
      if (success) {
        result.synced++;
      } else {
        result.failed++;
        result.errors.push(`Failed to sync post "${post.title}"`);
      }
    }

    // Then refresh cache from server
    try {
      await this.cacheFromServer(tenantId);
    } catch (error) {
      console.warn('[BoardOffline] Cache refresh after sync failed:', error);
    }

    result.success = result.failed === 0;
    return result;
  }

  /**
   * Clear all cached board post data for a tenant.
   */
  async clearCache(tenantId: string): Promise<void> {
    await db.cachedBoardPosts.where('tenant_id').equals(tenantId).delete();
  }

  /**
   * Clear only synced data, keeping any pending offline posts.
   */
  async clearSyncedCache(tenantId: string): Promise<void> {
    const synced = await db.cachedBoardPosts
      .where('tenant_id')
      .equals(tenantId)
      .filter(p => p.is_synced && !p.pending_changes)
      .toArray();

    const ids = synced.map(p => p.id);
    if (ids.length > 0) {
      await db.cachedBoardPosts.bulkDelete(ids);
    }
  }

  /**
   * Map a server BoardPostWithDetails response to the CachedBoardPost format.
   */
  private mapServerPostToCache(
    serverPost: BoardPostWithDetails,
    tenantId: string
  ): CachedBoardPost {
    return {
      id: serverPost.id,
      tenant_id: tenantId,
      incident_id: serverPost.incident_id || null,
      title: serverPost.title,
      content: serverPost.content,
      summary: serverPost.summary,
      classification: serverPost.classification,
      priority: serverPost.priority,
      target_audience: serverPost.target_audience,
      target_units: serverPost.target_units,
      effective_at: serverPost.effective_at,
      expires_at: serverPost.expires_at,
      is_pinned: serverPost.is_pinned,
      pinned_until: serverPost.pinned_until,
      author_id: serverPost.author_id,
      author_name: serverPost.author_name || serverPost.author?.name,
      author_unit: serverPost.author_unit,
      is_published: serverPost.is_published,
      tags: serverPost.tags || [],
      acknowledgement_count: serverPost.acknowledgement_count || 0,
      comment_count: serverPost.comment_count || 0,
      created_at: serverPost.created_at,
      updated_at: serverPost.updated_at,
      is_synced: true,
      synced_at: new Date().toISOString(),
      pending_changes: false,
    };
  }
}

export const boardOfflineService = new BoardOfflineService();
